import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveCollection, type CollectionName, type CollectionRow, type CollectionState } from "@/lib/siteCollections";

async function load(name: CollectionName): Promise<{ seeded: boolean; rows: CollectionRow[] }> {
  const [c, r] = await Promise.all([
    supabase.from("site_collections").select("collection").eq("collection", name).maybeSingle(),
    supabase.from("site_collection_items").select("item_key,data,sort_order,is_published")
      .eq("collection", name).eq("is_published", true).order("sort_order").limit(1000),
  ]);
  if (c.error) throw c.error;
  if (r.error) throw r.error;
  return { seeded: !!c.data, rows: (r.data ?? []) as CollectionRow[] };
}

/** Public items for a CMS collection; bundled fallback only while loading, on error, or before import. */
export function useSiteCollection<T>(name: CollectionName, fallback: T[]): { items: T[]; ready: boolean } {
  const q = useQuery({ queryKey: ["site-collection", name], queryFn: () => load(name), staleTime: 5 * 60_000, retry: 1 });
  const state: CollectionState = q.isSuccess ? { status: "success", ...q.data } : q.isError ? { status: "error" } : { status: "loading" };
  return { items: resolveCollection(state, fallback), ready: !q.isLoading };
}
